import { School, Product, Country, Centre } from "app/shared/model";
import { Fee } from "app/shared/model/Fee";
import { AgentCommissionDto, AgentCommissionRateQueryResult, AgentCommissionRateQueryDto, AgentCommissionVersionDto } from "app/shared/model/AgentCommissionDto";
import { CurrentAgentCommissionDialog } from "app/pages/AgentPricing/modules/EditDialogs";

export enum ActionType {
    ViewPageLoaded = 'AgentPricing/ViewPageLoaded',
    EditPageLoaded = 'AgentPricing/EditPageLoaded',
    ChangeTab = 'AgentPricing/ChangeTab',
    ChangeRateFinderCriteria = 'AgentPricing/ChangeRateFinderCriteria',
    RateFinderQueryResult = 'AgentPricing/RateFinderQueryResult',
    RateFinderQueryRunning = 'AgentPricing/RateFinderQueryRunning',
    ChangeRate = 'AgentPricing/ChangeRate',
    ChangeStartDate = 'AgentPricing/ChangeStartDate',
    ChangeEndDate = 'AgentPricing/ChangeEndDate',
    ChangeCurrentDialog = 'AgentPricing/ChangeCurrentDialog',
    ChangeSchoolOverrides = 'AgentPricing/ChangeSchoolOverrides',
    StartSavingRates = 'AgentPricing/StartSavingRates',
    SaveRatesSuccessful = 'AgentPricing/SaveRatesSuccessful',
    ErrorSavingRates = 'AgentPricing/ErrorSavingRates',
    RatesValidationError = 'AgentPricing/RatesValidationError',
    StartFetchPublishedVersions = 'AgentPricing/StartFetchPublishedVersions', 
    FetchPublishedVersionsSuccess = 'AgentPricing/FetchPublishedVersionsSuccess', 
    ErrorFetchingPublishedVersions = 'AgentPricing/ErrorFetchingPublishedVersions', 
    ChangeSelectedPublishedVersion = 'AgentPricing/ChangeSelectedPublishedVersion',
    Other = '__Other__'    
}

export interface ViewPageLoadedEvent {
    type: ActionType.ViewPageLoaded, 
    agentCommission: AgentCommissionDto,
    data: ViewPageLoadedData
}

export interface EditPageLoadedEvent {
    type: ActionType.EditPageLoaded,
    agentCommission: AgentCommissionDto,
    data: EditPageLoadedData
}

export interface EditPageLoadedData {
    id: string,
    name: string,
    countries: Country[],
    schools: School[],
    centres: Centre[],
    groupId?: string,
    hasNoCommissionGroup?: boolean,
    hasExternalAgentData?: boolean,
    status?: unknown
}

export interface ChangeTabEvent {
    type: ActionType.ChangeTab
    tab: AgentPricingTab
}

export interface RateFinderQueryResultEvent {
    type: ActionType.RateFinderQueryResult
    request: AgentCommissionRateQueryDto
    result: AgentCommissionRateQueryResult
}

export interface RateFinderQueryRunningEvent {
    type: ActionType.RateFinderQueryRunning
}

export interface ChangeRateFinderCriteriaEvent {
    type: ActionType.ChangeRateFinderCriteria
    itemId?: string
    itemType?: ItemType
    schoolId?: string
}

export interface ChangeRateAction {
    type: ActionType.ChangeRate
    columnId: string
    ruleId: string
    rate: string
}

export interface ChangeStartDateAction {
    type: ActionType.ChangeStartDate,
    newDate: string
}

export interface ChangeEndDateAction {
    type: ActionType.ChangeEndDate,
    newDate: string | null
}

export interface ChangeCurrentDialog {
    type: ActionType.ChangeCurrentDialog,
    dialog: CurrentAgentCommissionDialog
}

export interface ChangeSchoolOverrides {
    type: ActionType.ChangeSchoolOverrides,
    schoolOverrides: ChangeSchoolOverrideSpec[]
}

export interface ChangeSchoolOverrideSpec {
    countryCode: string
    schoolName: string
    schoolCode: string
}

export interface StartSavingRatesAction {
    type: ActionType.StartSavingRates,
    changeNumber: number
}

export interface RatesSavedSuccessfullyAction {
    type: ActionType.SaveRatesSuccessful,
    changeNumber: number
}

export interface RatesSaveErrorAction {
    type: ActionType.ErrorSavingRates,
    changeNumber: number
}

export interface RatesValidationErrorAction {
    type: ActionType.RatesValidationError,
    message: string
}

export interface StartFetchPublishedVersionsAction {
    type: ActionType.StartFetchPublishedVersions
}

export interface FetchPublishedVersionsSuccess {
    type: ActionType.FetchPublishedVersionsSuccess, 
    versions: AgentCommissionVersionDto[], 
    countries: Country[],
    schools: School[]
}

export interface ErrorFetchingPublishedVersionsAction {
    type: ActionType.ErrorFetchingPublishedVersions
}

export interface ChangeSelectedPublishedVersionAction {
    type: ActionType.ChangeSelectedPublishedVersion, 
    versionCode: string
}

export interface OtherAction {
    type: ActionType.Other
}

export type Action = 
    ViewPageLoadedEvent
    | EditPageLoadedEvent
    | ChangeTabEvent
    | ChangeRateFinderCriteriaEvent
    | RateFinderQueryResultEvent
    | RateFinderQueryRunningEvent
    | ChangeRateAction
    | ChangeStartDateAction
    | ChangeEndDateAction
    | ChangeCurrentDialog
    | ChangeSchoolOverrides
    | StartSavingRatesAction
    | RatesSavedSuccessfullyAction
    | RatesSaveErrorAction
    | RatesValidationErrorAction
    | StartFetchPublishedVersionsAction
    | FetchPublishedVersionsSuccess
    | ErrorFetchingPublishedVersionsAction
    | ChangeSelectedPublishedVersionAction
    | OtherAction;

export type ViewPageLoadedData = {
    id: string,
    name: string,
    countries: Country[],
    schools: School[],
    products: Product[],
    fees: Fee[],
    offices: { id: string; name: string; agentId: string }[],
    hasNoCommissionGroup?: boolean,
    hasExternalAgentData?: boolean,
    status?: unknown
}

export enum AgentPricingTab {
    GroupRates = "GroupRates",
    CompanyRates = "CompanyRates",
    VersionHistory = "VersionHistory"
}

export enum ItemType {
    Course = 'Course',
    Accommodation = 'Accommodation',
    AddOn = 'AddOn',
    Transfer = 'Transfer',
    Fee = 'Fee'
}
    
    